import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../store/hooks';
import { login } from '../store/slices/authSlice';
import jwtService from '../auth/jwt/jwtService';

const Login: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [rememberMe, setRememberMe] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');

        try {
            // Call the actual backend API
            const response = await jwtService.login({ email, password });

            // Dispatch login action with real data from backend
            dispatch(login({
                user: {
                    id: response.user.id,
                    email: response.user.email,
                    name: `${response.user.firstName} ${response.user.lastName}`,
                    role: response.user.role,
                    avatar: response.user.profileImage || undefined,
                },
                token: response.accessToken
            }));

            // Navigate to dashboard
            navigate('/');
        } catch (err: any) {
            console.error('Login error:', err);
            setError(err.message || t('login.loginFailed'));
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="bg-background min-h-screen flex items-center justify-center p-4 relative overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-5" style={{
                backgroundColor: '#1e293b',
                backgroundImage: 'radial-gradient(#334155 1px, transparent 1px)',
                backgroundSize: '20px 20px'
            }}></div>

            {/* Gradient Blobs */}
            <div className="absolute top-0 right-0 w-96 h-96 bg-primary/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-accent/10 rounded-full blur-3xl"></div>

            <div className="relative z-10 w-full max-w-6xl grid lg:grid-cols-2 gap-0 bg-white rounded-3xl shadow-2xl overflow-hidden">

                {/* Left Panel - Features */}
                <div className="hidden lg:flex flex-col justify-between p-12 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">

                    {/* Background Pattern */}
                    <div className="absolute inset-0 opacity-10" style={{
                        backgroundImage: 'radial-gradient(#ffffff 1px, transparent 1px)',
                        backgroundSize: '20px 20px'
                    }}></div>

                    {/* Gradient Blobs */}
                    <div className="absolute top-20 right-20 w-64 h-64 bg-primary/20 rounded-full blur-3xl"></div>
                    <div className="absolute bottom-20 left-20 w-64 h-64 bg-accent/20 rounded-full blur-3xl"></div>

                    <div className="relative z-10">
                        <div className="flex items-center gap-3 mb-8">
                            <img src="/logo.png" alt="Enjoy Triathlon" className="w-44 h-auto object-contain" />
                        </div>

                        <h2 className="text-4xl font-bold text-white mb-4 leading-tight">
                            {t('login.welcomeBack')}<br />{t('login.athlete')}
                        </h2>
                        <p className="text-slate-300 text-lg font-medium leading-relaxed">
                            {t('login.signInDesc')}
                        </p>
                    </div>

                    <div className="relative z-10 space-y-6">
                        <div className="flex items-start gap-4">
                            <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center text-primary flex-shrink-0">
                                <i className="fa-solid fa-chart-line text-xl"></i>
                            </div>
                            <div>
                                <h4 className="text-white font-bold mb-1">{t('login.trackProgress')}</h4>
                                <p className="text-slate-400 text-sm">{t('login.trackProgressDesc')}</p>
                            </div>
                        </div>

                        <div className="flex items-start gap-4">
                            <div className="w-12 h-12 rounded-xl bg-accent/20 flex items-center justify-center text-accent flex-shrink-0">
                                <i className="fa-solid fa-dumbbell text-xl"></i>
                            </div>
                            <div>
                                <h4 className="text-white font-bold mb-1">{t('login.customTraining')}</h4>
                                <p className="text-slate-400 text-sm">{t('login.customTrainingDesc')}</p>
                            </div>
                        </div>

                        <div className="flex items-start gap-4">
                            <div className="w-12 h-12 rounded-xl bg-highlight/20 flex items-center justify-center text-highlight flex-shrink-0">
                                <i className="fa-solid fa-trophy text-xl"></i>
                            </div>
                            <div>
                                <h4 className="text-white font-bold mb-1">{t('login.raceReady')}</h4>
                                <p className="text-slate-400 text-sm">{t('login.raceReadyDesc')}</p>
                            </div>
                        </div>
                    </div>

                    {/* Floating Icon */}
                    <div className="absolute bottom-0 right-0 opacity-5" style={{
                        animation: 'float 6s ease-in-out infinite'
                    }}>
                        <i className="fa-solid fa-person-biking text-[300px] text-white"></i>
                    </div>
                </div>

                {/* Right Panel - Login Form */}
                <div className="p-8 lg:p-12 flex flex-col justify-center">

                    {/* Mobile Logo */}
                    <div className="lg:hidden mb-8 text-center">
                        <div className="flex items-center justify-center gap-3 mb-4">
                            <img src="/logo.png" alt="Enjoy Triathlon" className="w-32 h-auto object-contain" />
                        </div>
                    </div>

                    <div className="mb-8">
                        <h1 className="text-3xl font-bold text-slate-800 mb-2">{t('login.signIn')}</h1>
                        <p className="text-slate-500 font-medium">{t('login.enterCredentials')}</p>
                    </div>

                    {/* Error Message */}
                    {error && (
                        <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg">
                            <div className="flex items-start gap-3">
                                <i className="fa-solid fa-circle-exclamation text-red-500 text-lg mt-0.5"></i>
                                <div>
                                    <h4 className="text-sm font-bold text-red-800 mb-1">{t('login.loginFailed')}</h4>
                                    <p className="text-sm text-red-700">{error}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-6">

                        {/* Email Field */}
                        <div className="space-y-2">
                            <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">
                                {t('login.email')} <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <i className="fa-solid fa-envelope absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none"></i>
                                <input
                                    type="email"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="marco.rossi@example.com"
                                    className="w-full pl-11 pr-4 py-3.5 bg-slate-50 border-2 border-slate-200 rounded-lg text-sm font-medium text-slate-700 focus:outline-none focus:bg-white focus:border-primary transition-all"
                                />
                            </div>
                        </div>

                        {/* Password Field */}
                        <div className="space-y-2">
                            <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">
                                {t('login.password')} <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <i className="fa-solid fa-lock absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none"></i>
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    required
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="Enter your password"
                                    className="w-full pl-11 pr-12 py-3.5 bg-slate-50 border-2 border-slate-200 rounded-lg text-sm font-medium text-slate-700 focus:outline-none focus:bg-white focus:border-primary transition-all"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                                >
                                    <i className={`fa-solid ${showPassword ? 'fa-eye-slash' : 'fa-eye'} text-sm`}></i>
                                </button>
                            </div>
                        </div>

                        {/* Remember Me. Forgot-password is intentionally hidden: passwords are
                            set by the admin, so there is no self-service reset. */}
                        <div className="flex items-center">
                            <label className="flex items-center gap-2 cursor-pointer group">
                                <input
                                    type="checkbox"
                                    checked={rememberMe}
                                    onChange={(e) => setRememberMe(e.target.checked)}
                                    className="w-4 h-4 rounded border-slate-300 text-primary focus:ring-primary focus:ring-offset-0 cursor-pointer"
                                />
                                <span className="text-sm font-medium text-slate-600 group-hover:text-slate-800">{t('login.rememberMe')}</span>
                            </label>
                        </div>

                        {/* Submit Button */}
                        <button
                            type="submit"
                            disabled={isLoading}
                            className="w-full py-4 bg-gradient-to-r from-primary to-cyan-600 hover:from-cyan-600 hover:to-primary text-white rounded-lg font-bold text-base shadow-lg shadow-cyan-500/30 hover:shadow-xl hover:shadow-cyan-500/40 transition-all duration-300 border-b-4 border-cyan-700 active:border-b-0 active:translate-y-1 flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isLoading ? (
                                <>
                                    <i className="fa-solid fa-spinner fa-spin"></i>
                                    <span>{t('login.signingIn')}</span>
                                </>
                            ) : (
                                <>
                                    <span>{t('login.signInBtn')}</span>
                                    <i className="fa-solid fa-arrow-right"></i>
                                </>
                            )}
                        </button>
                    </form>

                    {/* Footer Links */}
                    <div className="mt-8 pt-8 border-t border-slate-200">
                        <div className="flex items-center justify-center gap-6 text-slate-400">
                            <button type="button" className="hover:text-primary transition-colors">
                                <i className="fa-solid fa-shield-halved text-lg"></i>
                            </button>
                            <button type="button" className="hover:text-primary transition-colors text-xs font-medium">{t('login.privacyPolicy')}</button>
                            <span className="text-slate-300">•</span>
                            <button type="button" className="hover:text-primary transition-colors text-xs font-medium">{t('login.termsOfService')}</button>
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
                @keyframes float {
                    0%, 100% { transform: translateY(0px) rotate(0deg); }
                    50% { transform: translateY(-20px) rotate(5deg); }
                }
            `}</style>
        </div>
    );
};

export default Login;
